import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { NamedRedirect, NamedLink } from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import css from './SubscriptionPlansPage.module.css';

import { startCheckout, updateSubscription } from './SubscriptionPlansPage.duck';
import { bool } from 'prop-types';
import { propTypes } from '../../util/types';

import iconBasket from "../../assets/images/iconBasket.png";
import iconBag from "../../assets/images/iconBag.png";
import iconArrow from "../../assets/images/iconArrow.png";
import iconCheck from "../../assets/images/iconCheck.png";
import starterImg from "../../assets/images/Starter.png";
import growerImg from "../../assets/images/Grower.png";
import plusImg from "../../assets/images/MarketPro.png";

const plans = [
  {
    price_id: 'free',
    title: "Starter",
    planImg: starterImg,
    subtitle: "Free Forever",
    features: [
      "Perfect for new or seasonal local sellers getting started on the marketplace.",
      "Includes:",
      "• Up to 5 Fresh Picks per month",
      "• Your own Farmiez storefront",
      "• Local discovery by nearby shoppers",
      "• Shopper messaging (coming soon)",
      "• 0% Farmiez fees",
    ],
    price: "Free",
    button: "Start Selling",
    color: "green",
  },
  {
    price_id: process.env.REACT_APP_STRIPE_GROWER_PRICE_ID,
    title: "Grower",
    planImg: growerImg,
    subtitle: "$8.95 / month",
    features: [
      "Perfect for sellers ready to grow their visibility and increase sales",
      "Includes everything in Starter, plus:",
      "• Up to 50 Fresh Picks per month",
      "• Priority placement in local discovery",
      "• Trusted Grower badge",
      "• Farmiez marketing boosts",
      "• Featured in local promotions & emails",
      "• Advanced storefront features",
    ],
    price: "$8.95 / month",
    button: "Start Selling",
    color: "yellow",
  },
  {
    price_id: process.env.REACT_APP_STRIPE_PLUS_PRICE_ID,
    title: "Plus",
    planImg: plusImg,
    subtitle: "$14.95 / month",
    features: [
      "Ideal for sellers who want maximum visibility and consistent sales.",
      "Includes everything in Grower, plus:",
      "• Unlimited Fresh Picks",
      "• Homepage Featured Seller rotation",
      "• Premium social media promotion",
      "• Farmiez-designed marketing templates",
      "• Priority seller support",
      "Early access to new features",
      "• Early access to new platform features",
    ],
    price: "$14.95 / month",
    button: "Start Selling",
    color: "orange",
  },
];

export const SubscriptionPlansPageComponent = props => {
  const {
    startCheckout,
    startCheckoutInProgress,
    startCheckoutError,
    updateSubscription,
    updateSubscriptionInProgress,
    updateSubscriptionError,
    updateSubscriptionSuccess,
  } = props;

  const [inProgressPlan, setInProgressPlan] = useState('');

  const currentUser = useSelector(state => state.user.currentUser);
  const subscriptionData = currentUser?.attributes?.profile?.privateData?.stripeSubscription || null;
  const hasActiveSubscription = subscriptionData && subscriptionData.status === 'active';
  const purchasedPlanId = subscriptionData?.plan?.id;
  const isFreeActivated = currentUser?.attributes?.profile?.publicData?.activatedPlan === 'FREE';

  const handleSubscribe = (planData) => {
    const customerEmail = currentUser?.attributes?.email;
    const priceId = planData.price_id;

    setInProgressPlan(priceId)

    if (hasActiveSubscription && priceId !== 'free') {
      // If user already subscribed → update plan
      updateSubscription({
        subscriptionId: subscriptionData.id,
        priceId,
      });
    } else {
      // If no subscription yet → start checkout
      startCheckout({ priceId, customerEmail });
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp * 1000).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getPlanTitle = (priceId, hasActiveSubscription) => {
    if (!hasActiveSubscription) {
      return plans.find(p => p.price_id === 'free')?.title || 'Free Plan';
    }
    const plan = plans.find(p => p.price_id === priceId);
    return plan ? plan.title : 'Unknown Plan';
  };

  if (updateSubscriptionSuccess) {
    window.location.href = '/pricing';
    return
  }

  return (
    <div className={css.SubscriptionPlansPage}>
      <TopbarContainer />
      <div className={css.container}>
        <h1 className={css.pageTitle}>Farmiez Plans & Pricing</h1>
        <p className={css.pageText}>Simple. Neighborly. Just the way farming should be.</p>
        <p className={css.pageText}>
          Every seller starts free. As you grow, unlock more Fresh Picks and increase your visibility across the marketplace.
        </p>

        <section className={css.sectionWrapper}>
          <div className={css.FreshPickContainer}>

            {/* Heading */}
            <h3 className={css.heading}>What is a Fresh Pick?</h3>

            {/* Description */}
            <p className={css.description}>
              A Fresh Pick is one completed sale on Farmiez.
            </p>
            <p className={css.subDescription}>
              Each time a buyer successfully purchases one of your items,
              it counts as one Fresh Pick.
            </p>

            {/* Steps */}
            <div className={css.stepsWrapper}>

              {/* Step 1 */}
              <div className={css.step}>
                <div className={css.iconBox}>
                  <img src={iconBasket} alt="list product" />
                </div>
                <p className={css.stepText}>List a product</p>
              </div>

              {/* Arrow */}
              <div className={css.arrow}>
                <img src={iconArrow} alt="arrow" />
              </div>

              {/* Step 2 */}
              <div className={css.step}>
                <div className={css.iconBox}>
                  <img src={iconBag} alt="order" />
                </div>
                <p className={css.stepText}>
                  A neighbor places an order
                </p>
              </div>

              {/* Arrow */}
              <div className={css.arrow}>
                <img src={iconArrow} alt="arrow" />
              </div>

              {/* Step 3 */}
              <div className={css.step}>
                <div className={css.iconBox}>
                  <img src={iconCheck} alt="completed" />
                </div>
                <p className={css.stepText}>
                  Order completed = 1 Fresh Pick
                </p>
              </div>

            </div>

          </div>
        </section>

        {/* Show error message */}
        {/*{startCheckoutError ? (
          <p className={css.error}>
            Oops, something went wrong: {startCheckoutError.message || 'Please try again.'}
          </p>
        ) : null}*/}
        {updateSubscriptionError ? (
          <p className={css.error}>
            Could not update your subscription: {updateSubscriptionError.message || 'Please try again.'}
          </p>
        ) : null}

        {/* If user already has subscription, show subscription details */}
        {hasActiveSubscription ? (
          <div className={css.activeSubscriptionBox}>
            <h2>🎉 You’re currently subscribed!</h2>
            <p><strong>Plan:</strong> {getPlanTitle(subscriptionData?.plan?.id, hasActiveSubscription)}</p>
            <p><strong>Status:</strong> {subscriptionData.status}</p>
            <p><strong>Period:</strong> {formatDate(subscriptionData.current_period_start)} → {formatDate(subscriptionData.current_period_end)}</p>
          </div>
        ) : ( isFreeActivated ? (
            <div className={css.activeSubscriptionBox}>
              <h2>🌱 You’re on the Free Plan</h2>
              <p><strong>Plan:</strong> {getPlanTitle(null, hasActiveSubscription)}</p>
              <p><strong>Status:</strong> active</p>
            </div>
          ) : (
            null
          )
        )}

        <div className={css.plansGrid}>
          {plans.map((plan, idx) => {
            const isCurrentPlan = (purchasedPlanId === plan.price_id || (isFreeActivated && plan.price_id === 'free'));
            const isDisabled = isCurrentPlan || (!!purchasedPlanId && plan.price_id === 'free');

            return (
              <div key={idx} className={css.cardWrap}>
                {plan.title == "Grower" && 
                  <div className={css.mostPopularTag}>Most Popular</div>
                }
                <div
                  
                  className={`${css.card} ${css[plan.color]} ${isDisabled ? css.disabledCard : ''}`}
                >
                  <h2 className={css.title}>
                    <img src={plan.planImg} alt="icon" />
                    <span>{plan.title}</span>
                  </h2>
                  <p className={css.subtitle}>{plan.subtitle}</p>
                  <ul className={css.featureList}>
                    {plan.features.map((feat, i) => <li key={i}>{feat}</li>)}
                  </ul>
                  <div className={css.cardFooter}>
                    <p className={css.title}>{plan.price}</p>
                    {!!currentUser?.id ? (
                      <button
                        className={css.customBtn}
                        onClick={() => handleSubscribe(plan)}
                        disabled={
                          startCheckoutInProgress ||
                          updateSubscriptionInProgress ||
                          isDisabled
                        }
                      >
                        {((startCheckoutInProgress || updateSubscriptionInProgress) && inProgressPlan === plan.price_id)
                          ? 'Processing...'
                          : isCurrentPlan
                            ? 'Current Plan'
                            : plan.button}
                      </button>
                    ) : (
                      <NamedLink name="SignupPage" className={css.customBtn}>
                        {plan.button}
                      </NamedLink>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <FooterContainer />
    </div>
  );
};

SubscriptionPlansPageComponent.propTypes = {
  startCheckoutInProgress: bool,
  startCheckoutError: propTypes.error,
  updateSubscriptionInProgress: bool,
  updateSubscriptionError: propTypes.error,
};

const mapStateToProps = state => {
  const {
    startCheckoutInProgress,
    startCheckoutError,
    updateSubscriptionInProgress,
    updateSubscriptionError,
    updateSubscriptionSuccess, // <-- new
  } = state.SubscriptionPlansPage;
  return {
    startCheckoutInProgress,
    startCheckoutError,
    updateSubscriptionInProgress,
    updateSubscriptionError,
    updateSubscriptionSuccess,
  };
};

const mapDispatchToProps = { startCheckout, updateSubscription };

const SubscriptionPlansPage = compose(
  connect(mapStateToProps, mapDispatchToProps)
)(SubscriptionPlansPageComponent);

export default SubscriptionPlansPage;
